<script setup>
// Auto-extracted chrome for SolarPlanen.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Solar Planen" },
    brandTagline: { type: String, default: "Dach. Modul. Plan." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Solar Planen");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Dach. Modul. Plan.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Inter:wght@400;500;600;700;800&display=swap";
</script>

<template>
    <div class="spl" :style="`--spl-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="spl-top">
                    <div class="spl-top__inner">
                        <a class="spl-brand" href="/">
                            <span class="spl-brand__bar"></span>
                            <span class="spl-brand__name">{{ brandName }}</span>
                            <span class="spl-brand__tag">{{ brandTagline }}</span>
                        </a>
                        <a href="#spl-stage" class="spl-top__cta">Dach planen</a>
                    </div>
                </header>
        <slot />
        <footer class="spl-foot">
                    <div class="spl-foot__inner">
                        <span>{{ brandName }} &middot; {{ brandTagline }}</span>
                        <nav><a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a><a href="/agb">AGB</a><a href="/widerruf">Widerruf</a><a href="/ratgeber">Ratgeber</a></nav>
                        <p>&copy; {{ new Date().getFullYear() }} solar-planen.de</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.spl { --spl-paper: #fefef8; --spl-ink: #1c1917; --spl-sub: #57534e; --spl-line: #e7e5e0;
    font-family: 'Inter', system-ui, sans-serif; color: var(--spl-ink); background: var(--spl-paper); min-height: 100dvh; line-height: 1.55; -webkit-font-smoothing: antialiased; }
.spl *, .spl *::before, .spl *::after { box-sizing: border-box; }
.spl h1, .spl h2, .spl h3 { font-family: 'DM Serif Display', Georgia, serif; font-weight: 400; letter-spacing: -0.01em; line-height: 1.05; margin: 0; }
.spl h1 em, .spl h2 em { font-style: italic; color: var(--spl-accent); }

.spl-top { border-bottom: 1px solid var(--spl-line); background: var(--spl-paper); position: sticky; top: 0; z-index: 30; }
.spl-top__inner { max-width: 1240px; margin: 0 auto; padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.spl-brand { display: inline-flex; align-items: center; gap: 10px; color: var(--spl-ink); text-decoration: none; font-size: 14px; }
.spl-brand__bar { width: 3px; height: 22px; background: var(--spl-accent); display: inline-block; }
.spl-brand__name { font-family: 'DM Serif Display', serif; font-size: 18px; }
.spl-brand__tag { color: var(--spl-sub); font-size: 13px; }
.spl-top__cta { background: var(--spl-accent); color: #fff; text-decoration: none; padding: 10px 18px; font-size: 13px; font-weight: 600; border-radius: 4px; transition: background 200ms ease; }
.spl-top__cta:hover { background: #4d7c0f; }

.spl-hero { max-width: 1240px; margin: 0 auto; padding: 64px 24px 56px; display: grid; grid-template-columns: 1fr; gap: 48px; align-items: center; }
@media (min-width: 980px) { .spl-hero { grid-template-columns: 1.4fr 1fr; padding: 96px 24px 80px; gap: 64px; } }
.spl-hero__kicker { font-size: 12px; color: var(--spl-sub); letter-spacing: 0.08em; margin-bottom: 18px; }
.spl-hero__kicker span { color: var(--spl-accent); font-weight: 700; }
.spl-hero__h { font-size: clamp(44px, 8vw, 88px); text-wrap: balance; margin-bottom: 16px; }
.spl-hero__sub { font-size: 17px; color: var(--spl-sub); max-width: 56ch; margin-bottom: 28px; }
.spl-cta-primary { display: inline-flex; align-items: center; gap: 10px; background: var(--spl-accent); color: #fff; border: none; padding: 16px 28px; font-size: 16px; font-weight: 600; border-radius: 6px; cursor: pointer; font-family: inherit; transition: all 220ms cubic-bezier(0.32,0.72,0,1); }
.spl-cta-primary:hover { background: #4d7c0f; transform: translateY(-1px); }

.spl-hero__preview { display: flex; justify-content: center; }
.spl-roof { padding: 32px; background: #fff; border: 1px solid var(--spl-line); border-radius: 8px; box-shadow: 0 8px 24px -16px rgba(28, 25, 23, 0.2); }
.spl-roof__plate { display: flex; flex-direction: column; gap: 4px; transform: perspective(800px) rotateX(18deg); transform-origin: center bottom; }
.spl-roof__row { display: flex; gap: 4px; }
.spl-roof__mod { width: 40px; height: 28px; background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%); border-radius: 2px; border: 1px solid #475569; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05); }
.spl-roof__lbl { text-align: center; font-size: 12px; color: var(--spl-sub); margin-top: 18px; letter-spacing: 0.04em; }

.spl-stage { padding: 48px 24px; background: #fff; border-top: 1px solid var(--spl-line); border-bottom: 1px solid var(--spl-line); }
.spl-stage__inner { max-width: 1240px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 32px; }
@media (min-width: 980px) { .spl-stage__inner { grid-template-columns: 1.6fr 1fr; gap: 48px; align-items: start; } }

.spl-stage__col--form { background: var(--spl-paper); padding: 36px; border: 1px solid var(--spl-line); border-radius: 8px; }
.spl-stepHead { margin-bottom: 28px; }
.spl-stepHead__no { font-size: 12px; color: var(--spl-accent); font-weight: 700; letter-spacing: 0.12em; }
.spl-stepHead h2 { font-size: clamp(28px, 4vw, 42px); margin-top: 8px; }
.spl-stepHead p { color: var(--spl-sub); font-size: 15px; margin-top: 6px; }
.spl-stepHead__bar { height: 2px; background: var(--spl-line); margin-top: 18px; position: relative; overflow: hidden; }
.spl-stepHead__bar span { position: absolute; left: 0; top: 0; bottom: 0; background: var(--spl-accent); transition: width 320ms cubic-bezier(0.32,0.72,0,1); }

.spl-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.spl-num { display: flex; flex-direction: column; gap: 6px; }
.spl-num > span { font-size: 13px; font-weight: 600; color: var(--spl-ink); }
.spl-numCtl { display: flex; align-items: center; border: 1px solid var(--spl-line); border-radius: 6px; background: #fff; overflow: hidden; }
.spl-numCtl input { flex: 1; border: none; padding: 14px 16px; font-size: 16px; font-family: inherit; background: transparent; }
.spl-numCtl input:focus { outline: none; }
.spl-numCtl em { font-style: normal; padding: 0 14px; color: var(--spl-sub); font-size: 14px; border-left: 1px solid var(--spl-line); }

.spl-chips { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
@media (max-width: 540px) { .spl-chips { grid-template-columns: repeat(2, 1fr); } }
.spl-chip { background: #fff; border: 1px solid var(--spl-line); border-radius: 6px; padding: 16px 12px; cursor: pointer; font-family: inherit; display: flex; flex-direction: column; gap: 4px; align-items: center; transition: all 180ms ease; }
.spl-chip:hover { border-color: var(--spl-accent); }
.spl-chip--on { background: var(--spl-accent); color: #fff; border-color: var(--spl-accent); }
.spl-chip__l { font-weight: 700; font-size: 16px; }
.spl-chip__h { font-size: 12px; opacity: 0.7; }

.spl-tilt input[type=range] { width: 100%; accent-color: var(--spl-accent); }
.spl-tilt__row { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; color: var(--spl-sub); margin-top: 10px; }
.spl-tilt__val { font-family: 'DM Serif Display', serif; font-size: 28px; color: var(--spl-accent); }
.spl-tilt__hint { color: var(--spl-sub); font-size: 13px; margin-top: 8px; }

.spl-modules { display: grid; grid-template-columns: 1fr; gap: 12px; }
.spl-mod { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; background: #fff; border: 1px solid var(--spl-line); border-radius: 6px; cursor: pointer; transition: all 180ms ease; font-family: inherit; text-align: left; }
.spl-mod:hover { border-color: var(--spl-accent); }
.spl-mod--on { background: var(--spl-accent); color: #fff; border-color: var(--spl-accent); }
.spl-mod__big { font-weight: 700; font-size: 17px; }
.spl-mod__hint { font-size: 13px; opacity: 0.75; }

.spl-stepNav { display: flex; justify-content: space-between; align-items: center; margin-top: 28px; }
.spl-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit; font-weight: 600; padding: 12px 22px; border-radius: 6px; border: 1px solid transparent; cursor: pointer; transition: all 200ms ease; text-decoration: none; font-size: 14px; }
.spl-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.spl-btn--primary { background: var(--spl-accent); color: #fff; }
.spl-btn--primary:hover:not(:disabled) { background: #4d7c0f; }
.spl-btn--ghost { background: transparent; color: var(--spl-sub); border-color: var(--spl-line); }
.spl-btn--ghost:hover { color: var(--spl-ink); border-color: var(--spl-ink); }
.spl-btn--lg { padding: 16px 28px; font-size: 16px; }
.spl-btn--sm { padding: 8px 14px; font-size: 13px; }
.spl-btn--full { width: 100%; }

.spl-resultPanel { text-align: left; }
.spl-resultPanel__eye { font-size: 12px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--spl-sub); font-weight: 700; margin-bottom: 10px; }
.spl-resultPanel__big { font-family: 'DM Serif Display', serif; font-size: clamp(64px, 10vw, 96px); line-height: 1; color: var(--spl-accent); margin-bottom: 4px; }
.spl-resultPanel__big em { font-style: normal; font-size: 0.36em; color: var(--spl-sub); margin-left: 12px; font-family: 'Inter', sans-serif; font-weight: 500; }
.spl-resultPanel__layout { color: var(--spl-sub); font-size: 15px; margin-bottom: 24px; }
.spl-resultPanel__kpi { list-style: none; padding: 22px 0; margin: 0 0 28px; border-top: 1px solid var(--spl-line); border-bottom: 1px solid var(--spl-line); display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
@media (max-width: 540px) { .spl-resultPanel__kpi { grid-template-columns: 1fr; } }
.spl-resultPanel__kpi li { display: flex; flex-direction: column; gap: 4px; }
.spl-resultPanel__kpi span { font-size: 12px; color: var(--spl-sub); text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; }
.spl-resultPanel__kpi strong { font-size: 20px; font-weight: 700; }

.spl-leadBlock h3 { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 6px; }
.spl-leadBlock > p { color: var(--spl-sub); margin-bottom: 20px; font-size: 15px; }
.spl-form { display: grid; gap: 14px; }
.spl-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.spl-input { display: flex; flex-direction: column; gap: 6px; }
.spl-input > span { font-size: 13px; font-weight: 600; }
.spl-input em { font-weight: 400; color: var(--spl-sub); font-style: normal; }
.spl-input input { padding: 14px 16px; font-size: 16px; border: 1px solid var(--spl-line); border-radius: 6px; background: #fff; font-family: inherit; }
.spl-input input:focus { outline: none; border-color: var(--spl-accent); }
.spl-input--err input { border-color: #b91c1c; }
.spl-consentBox { display: flex; gap: 12px; padding: 14px; background: var(--spl-paper); border: 1px solid var(--spl-line); border-radius: 6px; font-size: 13px; color: var(--spl-sub); line-height: 1.5; cursor: pointer; }
.spl-consentBox input { margin-top: 2px; flex-shrink: 0; accent-color: var(--spl-accent); }
.spl-err { color: #b91c1c; font-size: 13px; text-align: center; }
.spl-back { margin-top: 18px; }

.spl-doneBlock { text-align: center; padding: 24px 0; }
.spl-doneBlock__big { font-family: 'DM Serif Display', serif; font-size: 32px; margin-bottom: 8px; }

.spl-sideCard { background: #1c1917; color: #f5f5f4; border-radius: 8px; padding: 28px; position: sticky; top: 88px; }
.spl-sideCard__eye { font-size: 11px; text-transform: uppercase; letter-spacing: 0.18em; color: var(--spl-accent); font-weight: 700; margin-bottom: 14px; }
.spl-sideCard__big { font-family: 'DM Serif Display', serif; font-size: 52px; line-height: 1; }
.spl-sideCard__big em { font-style: normal; font-size: 0.4em; color: #a8a29e; margin-left: 8px; font-family: 'Inter', sans-serif; font-weight: 500; }
.spl-sideCard__hint { color: #a8a29e; font-size: 14px; margin: 8px 0 18px; }
.spl-sideCard ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; padding-top: 16px; border-top: 1px solid #292524; }
.spl-sideCard li { display: flex; justify-content: space-between; align-items: baseline; font-size: 14px; }
.spl-sideCard li span { color: #a8a29e; }
.spl-sideCard li strong { font-weight: 700; }
.spl-sideCard__win { color: #a3e635; }
.spl-sideCard__empty { color: #a8a29e; font-size: 14px; }

.spl-meth { padding: 96px 24px; background: var(--spl-paper); }
.spl-meth__inner { max-width: 1120px; margin: 0 auto; }
.spl-meth__head { margin-bottom: 56px; max-width: 700px; }
.spl-meth__head p { color: var(--spl-accent); font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; font-size: 12px; margin-bottom: 12px; }
.spl-meth__head h2 { font-size: clamp(32px, 5vw, 52px); }
.spl-meth__rows { display: grid; grid-template-columns: 1fr; gap: 0; }
.spl-meth__row { display: grid; grid-template-columns: auto 1fr auto; gap: 28px; padding: 36px 0; border-top: 1px solid var(--spl-line); align-items: start; }
.spl-meth__row:last-child { border-bottom: 1px solid var(--spl-line); }
.spl-meth__no { font-family: 'DM Serif Display', serif; font-size: 56px; color: var(--spl-accent); line-height: 1; }
.spl-meth__row h3 { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 8px; }
.spl-meth__row p { color: var(--spl-sub); font-size: 15px; max-width: 56ch; }
.spl-meth__tag { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--spl-sub); padding: 4px 10px; border: 1px solid var(--spl-line); border-radius: 999px; align-self: start; font-weight: 600; }
@media (max-width: 720px) { .spl-meth__row { grid-template-columns: auto 1fr; } .spl-meth__tag { grid-column: 2; justify-self: start; margin-top: 8px; } }

.spl-faq { padding: 80px 24px; background: #fff; border-top: 1px solid var(--spl-line); }
.spl-faq__inner { max-width: 900px; margin: 0 auto; }
.spl-faq h2 { font-size: clamp(28px, 4vw, 40px); margin-bottom: 36px; max-width: 24ch; }
.spl-faq dl { display: grid; gap: 28px; }
.spl-faq dt { font-weight: 700; font-family: 'Inter', sans-serif; margin-bottom: 6px; font-size: 16px; }
.spl-faq dd { color: var(--spl-sub); margin: 0; font-size: 15px; }

.spl-foot { background: #fafaf9; border-top: 1px solid var(--spl-line); padding: 32px 24px; }
.spl-foot__inner { max-width: 1240px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; color: var(--spl-sub); flex-wrap: wrap; }
.spl-foot nav { display: flex; gap: 18px; }
.spl-foot a { color: var(--spl-sub); text-decoration: none; }
.spl-foot a:hover { color: var(--spl-ink); }

.spl-stick { position: fixed; left: 0; right: 0; bottom: 0; padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); background: var(--spl-accent); color: #fff; display: flex; justify-content: space-between; align-items: center; gap: 16px; z-index: 40; font-size: 14px; }
.spl-stick .spl-btn { background: #fff; color: var(--spl-accent); }
.spl-stick .spl-btn:hover { background: #f0fdf4; }
@media (min-width: 980px) { .spl-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .spl *, .spl *::before, .spl *::after { transition: none !important; animation: none !important; transform: none !important; }
}

/* ── Trust strip (images + cites + byline) — spl-* unique treatment ── */
.spl-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.spl-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .spl-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.spl-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid #65A30D55; box-shadow: 0 0 28px -4px #65A30D55, 0 10px 24px -16px rgba(20,17,13,0.22);
}
.spl-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.spl-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.spl-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.spl-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.spl-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.spl-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #65A30D55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.spl-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.spl-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #65A30D;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.spl-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.spl-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.spl-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.spl-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.spl-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.spl-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
